import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { MUMBAI_LOCATIONS, INDIAN_BANKS, RELATIONS } from '../lib/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DriverRegistration() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [driverId, setDriverId] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    mobile: '',
    location: '',
    pan_number: '',
    pan_image_url: '',
    aadhar_number: '',
    aadhar_front_url: '',
    aadhar_back_url: '',
    license_number: '',
    license_front_url: '',
    license_back_url: '',
    selfie_url: '',
    bank_details: {
      account_holder_name: '',
      account_number: '',
      ifsc: '',
      bank_name: '',
    },
    reference1: {
      name: '',
      mobile: '',
      relation: '',
    },
    reference2: {
      name: '',
      mobile: '',
      relation: '',
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleFileUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    setUploading((prev) => ({ ...prev, [fieldName]: true }));

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/driver-registration/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const fileUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData((prev) => ({ ...prev, [fieldName]: fileUrl }));
      toast.success('Document uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Document upload failed');
    } finally {
      setUploading((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all documents are uploaded
    const requiredDocs = ['pan_image_url', 'aadhar_front_url', 'aadhar_back_url', 'license_front_url', 'license_back_url', 'selfie_url'];
    const missingDocs = requiredDocs.filter(doc => !formData[doc]);
    
    if (missingDocs.length > 0) {
      toast.error('Please upload all required documents');
      return;
    }

    // Validate select fields
    if (!formData.location) {
      toast.error('Please select your location');
      return;
    }
    if (!formData.bank_details.bank_name) {
      toast.error('Please select your bank');
      return;
    }
    if (!formData.reference1.relation) {
      toast.error('Please select relation for Reference 1');
      return;
    }
    if (!formData.reference2.relation) {
      toast.error('Please select relation for Reference 2');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/driver-registration/submit`, formData);
      setDriverId(response.data.driver_id);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const FileUploadField = ({ label, fieldName, accept = 'image/*' }) => (
    <div>
      <Label htmlFor={fieldName}>
        {label} <span className="text-red-500">*</span>
      </Label>
      <div className="mt-1 flex items-center gap-3">
        <Input
          id={fieldName}
          type="file"
          accept={accept}
          onChange={(e) => handleFileUpload(e, fieldName)}
          className="flex-1"
          disabled={uploading[fieldName]}
          data-testid={`upload-${fieldName}`}
        />
        {uploading[fieldName] && <Loader2 className="animate-spin" size={20} />}
        {formData[fieldName] && !uploading[fieldName] && (
          <CheckCircle className="text-green-500" size={20} />
        )}
      </div>
      {formData[fieldName] && (
        <div className="mt-2">
          <img
            src={formData[fieldName]}
            alt={label}
            className="w-32 h-32 object-cover rounded border border-gray-300"
          />
        </div>
      )}
    </div>
  );

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="mb-6">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h1 className="text-3xl font-black font-heading text-gray-900 mb-2">
              Registration Submitted Successfully!
            </h1>
            <p className="text-gray-600">
              Thank you for applying to join Shizah Mobility
            </p>
          </div>

          <div className="bg-blue-50 p-6 rounded-lg mb-6">
            <p className="text-sm text-gray-600 mb-2">Your Driver ID</p>
            <p className="text-2xl font-bold text-shizah-blue">{driverId}</p>
          </div>

          <div className="text-left bg-gray-50 p-6 rounded-lg mb-6">
            <h3 className="font-bold text-gray-900 mb-3">What's Next?</h3>
            <ol className="space-y-2 text-gray-700 text-sm">
              <li>✓ Your application has been received</li>
              <li>✓ Our team will review your documents</li>
              <li>✓ We'll contact you within 2-3 business days</li>
              <li>✓ Keep your phone available for verification call</li>
            </ol>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Please save your Driver ID: <strong>{driverId}</strong> for future reference
          </p>

          <Button
            onClick={() => window.location.href = '/'}
            className="bg-shizah-blue hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="text-center">
            <img
              src="https://customer-assets.emergentagent.com/job_9308f2ee-4a90-4036-bc1e-09ce5941433b/artifacts/cosm4gyf_Shizah.jpeg"
              alt="Shizah Mobility"
              className="w-48 mx-auto mb-4"
            />
            <h1 className="text-3xl font-black font-heading text-gray-900 mb-2">
              Driver Registration Form
            </h1>
            <p className="text-gray-600">
              Join Shizah Mobility - Your Comfort, Our Priority
            </p>
            <p className="text-sm text-red-500 mt-2">All fields are mandatory *</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="full_name">Full Name <span className="text-red-500">*</span></Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <Label htmlFor="mobile">Mobile Number <span className="text-red-500">*</span></Label>
                <Input
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  pattern="[0-9]{10}"
                  placeholder="10-digit mobile number"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Location <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.location}
                  onValueChange={(value) => setFormData({ ...formData, location: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your location" />
                  </SelectTrigger>
                  <SelectContent>
                    {MUMBAI_LOCATIONS.map((loc) => (
                      <SelectItem key={loc} value={loc}>
                        {loc}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">Documents</h2>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="pan_number">PAN Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="pan_number"
                    name="pan_number"
                    value={formData.pan_number}
                    onChange={handleChange}
                    required
                    pattern="[A-Z]{5}[0-9]{4}[A-Z]{1}"
                    placeholder="ABCDE1234F"
                  />
                </div>
                <FileUploadField label="PAN Card Image" fieldName="pan_image_url" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="aadhar_number">Aadhar Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="aadhar_number"
                    name="aadhar_number"
                    value={formData.aadhar_number}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{12}"
                    placeholder="12-digit Aadhar number"
                  />
                </div>
                <div className="space-y-4">
                  <FileUploadField label="Aadhar Front" fieldName="aadhar_front_url" />
                  <FileUploadField label="Aadhar Back" fieldName="aadhar_back_url" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="license_number">Driving License <span className="text-red-500">*</span></Label>
                  <Input
                    id="license_number"
                    name="license_number"
                    value={formData.license_number}
                    onChange={handleChange}
                    required
                    placeholder="License number"
                  />
                </div>
                <div className="space-y-4">
                  <FileUploadField label="License Front" fieldName="license_front_url" />
                  <FileUploadField label="License Back" fieldName="license_back_url" />
                </div>
              </div>

              <FileUploadField label="Your Selfie" fieldName="selfie_url" />
            </div>
          </div>

          {/* Bank Details */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">Bank Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label htmlFor="bank_details.account_holder_name">Account Holder Name <span className="text-red-500">*</span></Label>
                <Input
                  id="bank_details.account_holder_name"
                  name="bank_details.account_holder_name"
                  value={formData.bank_details.account_holder_name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="bank_details.account_number">Account Number <span className="text-red-500">*</span></Label>
                <Input
                  id="bank_details.account_number"
                  name="bank_details.account_number"
                  value={formData.bank_details.account_number}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <Label htmlFor="bank_details.ifsc">IFSC Code <span className="text-red-500">*</span></Label>
                <Input
                  id="bank_details.ifsc"
                  name="bank_details.ifsc"
                  value={formData.bank_details.ifsc}
                  onChange={handleChange}
                  required
                  pattern="[A-Z]{4}[0-9]{7}"
                  placeholder="SBIN0001234"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Bank Name <span className="text-red-500">*</span></Label>
                <Select
                  value={formData.bank_details.bank_name}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      bank_details: { ...prev.bank_details, bank_name: value },
                    }))
                  }
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDIAN_BANKS.map((bank) => (
                      <SelectItem key={bank} value={bank}>
                        {bank}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* References */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold font-heading text-gray-900 mb-4">References</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Reference 1</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="reference1.name">Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="reference1.name"
                      name="reference1.name"
                      value={formData.reference1.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="reference1.mobile">Mobile <span className="text-red-500">*</span></Label>
                    <Input
                      id="reference1.mobile"
                      name="reference1.mobile"
                      value={formData.reference1.mobile}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                    />
                  </div>
                  <div>
                    <Label>Relation <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.reference1.relation}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          reference1: { ...prev.reference1, relation: value },
                        }))
                      }
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select relation" />
                      </SelectTrigger>
                      <SelectContent>
                        {RELATIONS.map((rel) => (
                          <SelectItem key={rel} value={rel}>
                            {rel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Reference 2</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="reference2.name">Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="reference2.name"
                      name="reference2.name"
                      value={formData.reference2.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="reference2.mobile">Mobile <span className="text-red-500">*</span></Label>
                    <Input
                      id="reference2.mobile"
                      name="reference2.mobile"
                      value={formData.reference2.mobile}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                    />
                  </div>
                  <div>
                    <Label>Relation <span className="text-red-500">*</span></Label>
                    <Select
                      value={formData.reference2.relation}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          reference2: { ...prev.reference2, relation: value },
                        }))
                      }
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select relation" />
                      </SelectTrigger>
                      <SelectContent>
                        {RELATIONS.map((rel) => (
                          <SelectItem key={rel} value={rel}>
                            {rel}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-shizah-blue hover:bg-blue-700 text-white py-4 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={24} />
                  Submitting Registration...
                </>
              ) : (
                'Submit Registration'
              )}
            </Button>
            <p className="text-xs text-gray-500 text-center mt-3">
              By submitting, you agree to our terms and conditions
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
